import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, TextField, Grid, Card, CardContent, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh } from '@mui/icons-material';
import CustomPagination from '../common/CustomPagination';
import { ImagePreviewPortal } from '../../pages/Orders/columns';
import { fetchOrders } from '../../store/slices/orderSlice';

const OrdersTable = ({
  orders,
  loading,
  page,
  rowsPerPage,
  totalPages,
  setPage,
  setRowsPerPage,
  customRowsActive,
  customRowsInput,
  setCustomRowsActive,
  setCustomRowsInput,
  handleCustomRowsInputChange,
  handleCustomRowsInputBlur,
  columns,
  filterUsername,
  filterPhone,
  filterBinCode,
  filterWarehouseNumber,
  filterOrderNumber,
  filterPurchasingAccount,
  filterPoolId,
  onFilterUsernameChange,
  onFilterPhoneChange,
  onFilterBinCodeChange,
  onFilterWarehouseNumberChange,
  onFilterOrderNumberChange,
  onFilterPurchasingAccountChange,
  onFilterPoolIdChange,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const dispatch = useDispatch();
  const lastFetchParams = useSelector((state) => state.order.lastFetchParams);

  const parseTotalValue = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
      const normalized = value.replace(/[^\d.-]/g, '');
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? numeric : 0;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const totalColumnSum = orders.reduce((sum, order) => {
    const rowTotal = order?.total_price_raw ?? order?.total_price;
    return sum + parseTotalValue(rowTotal);
  }, 0);

  const formattedTotal = `¥${totalColumnSum.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchOrders(lastFetchParams || {}));
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <ImagePreviewPortal />
      
      {/* Filters */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('orders:filters.username')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterUsername}
                onChange={(e) => {
                  setPage(0);
                  onFilterUsernameChange(e);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('orders:filters.phone')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterPhone}
                onChange={(e) => {
                  setPage(0);
                  onFilterPhoneChange(e);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('orders:filters.binCode')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterBinCode}
                onChange={(e) => {
                  setPage(0);
                  onFilterBinCodeChange(e);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('orders:filters.warehouseNumber')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterWarehouseNumber}
                onChange={(e) => {
                  setPage(0);
                  onFilterWarehouseNumberChange(e);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('orders:filters.orderNumber')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterOrderNumber}
                onChange={(e) => {
                  setPage(0);
                  onFilterOrderNumberChange(e);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('orders:filters.purchasingAccount')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterPurchasingAccount}
                onChange={(e) => {
                  setPage(0);
                  onFilterPurchasingAccountChange?.(e);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('orders:filters.poolId')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterPoolId}
                onChange={(e) => {
                  setPage(0);
                  onFilterPoolIdChange(e);
                }}
                
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, height: '100%', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  {t('orders:actions.refreshOrders')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={orders}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200, // Ensure minimum width for all columns
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
            <Box
              sx={{
                minWidth: 1200,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
                px: 2,
                py: 1.5,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('orders:table.total')}: {formattedTotal}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default OrdersTable; 
